<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { menuRoutesEnum, getDocumentRouteByDocumentType } from '@/router'

import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxSplitter, DxItem } from 'devextreme-vue/splitter'

import { useAddressRef, useLabelController } from '@/composables/data-grid-column'
import { useColumns, useDialogTitle } from '@/composables/sale/sale-document-grid'
import { useDocumentIdentifiers } from '@/composables/document'
import { useMultiSelectFilter, useMultiSelectEditorOptions } from '@/composables/data-grid'
import { useNotify } from '@/composables/notify'
import { usePartialSave } from '@/composables/partial-save'
import { usePrint } from '@/composables/print'
import { clearRemovedRoundingRowFlag } from '@/composables/rounding-row'
import { useValidationTabs } from '@/composables/validation'
import { useDialog } from '@/composables/dialog'
import * as dataGrid from '@/composables/data-grid'
import { useAttachment } from '@/composables/registry/service-attachment-grid'

import { registryApi } from '@/apis/registry.api'
import { saleApi } from '@/apis/sale.api'

import { usePortalStore } from '@/stores/portal.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useSaleStore } from '@/stores/sale.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useSubjectService } from '@/services/subject.service'
import { useEntityLock } from '@/services/entity-lock.service'
import { useDocumentService } from '@/services/document.service'

import AttachmentIconList from '@/components/shared/AttachmentIconList.vue'
import CityDropDownBox from '@/components/shared/CityDropDownBox.vue'
import CountryDropDownBox from '@/components/shared/CountryDropDownBox.vue'
import CustomerDropDownBox from '@/components/shared/CustomerDropDownBox.vue'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import CustomToolbarDeleteButtonTemplate from '@/components/shared/CustomToolbarDeleteButtonTemplate.vue'
import DeliveryAddressDropDownBox from '@/components/shared/DeliveryAddressDropDownBox.vue'
import DepositDropDownBox from '@/components/shared/DepositDropDownBox.vue'
import DocumentGenerateDialog from '@/components/shared/DocumentGenerateDialog.vue'
import DocumentTagsTagBox from '@/components/shared/DocumentTagsTagBox.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'
import InformationToolTip from '@/components/shared/InformationToolTip.vue'
import MechanicDropDownBox from '@/components/shared/MechanicDropDownBox.vue'
import MechanicsAvatarList from '@/components/shared/MechanicsAvatarList.vue'
import MultiSelectFilter from '@/components/shared/MultiSelectFilter.vue'
import PrintDialog from '@/components/shared/PrintDialog.vue'
import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import SaleDocumentRowGrid from '@/components/sale/SaleDocumentRowGrid.vue'
import StateDropDownBox from '@/components/shared/StateDropDownBox.vue'
import SupplierDropDownBox from '@/components/shared/SupplierDropDownBox.vue'
import ValidationTab from '@/components/shared/ValidationTab.vue'
import VehicleDropDownBox from '@/components/shared/VehicleDropDownBox.vue'

const props = defineProps({
  type: {
    type: String,
    required: true
  }
})

const { genericDialog, customDialog } = useDialog()

const gridId = 'sale-document-grid'
const formInstance = ref(null)
const mainSplitterId = 'sale-document-grid-main-splitter'
const selectedDocument = ref(null)
const selectedRows = ref([])

const saleDocumentTypes = ref([])

const { route, router } = dataGrid.useGridRouter()
const i18n = getI18n()
const { notifyError } = useNotify()

const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()
const portalStore = usePortalStore()
const saleStore = useSaleStore()

const { extractReferenceData, setSdiDocumentTypeValue } = useDocumentService()
const { getAttachmentEntities } = useAttachment()
const print = usePrint()

let generateDocumentToolbarButton = null
const generateDialogVisible = ref(false)
const generateDocumentType = ref(null)
const refDocument = ref(null)
const currentCustomer = ref(null)
const currentSaleDocumentType = ref(null)
const currentSaleDocumentPage = ref(props.type)
const isCustomerSaveDisabled = ref(true)
const isReloadCustomerDataAllowed = ref(true)
const isReloadDeliveryAddressDataAllowed = ref(true)
const currentCustomerTypeId = ref(null)
const currentCustomerSubTypeId = ref(null)
const currentCarService = ref(null)
const currentVehicleFuelType = ref(null)
const isDeliveryAddressSaveDisabled = ref(true)
const isBillingCustomerDisabled = ref(true)
const isReadOnly = ref(false)
const hasRows = ref(false)

const subjectTypePARef = registryStore.subjectTypes.find(st => st.code === 'EPA')?.id
const isPaid = ref(false)
const includesVat = ref(false)
const localizedDocumentTypes = portalStore.getLocalizedDocumentTypes()

const editors = {}

let firstEditLoad = false
let defaultToolbar = true

const editingTabPanelInstance = ref(null)

const disabledCustomerFields = ['subject_type_ref', 'subject_sub_type_ref', 'name', 'mobile']
const disabledDeliveryAddressFields = ['name', 'address.street', 'address.country', ['address.state', 'address.state_text'], 'address.zip_code', ['address.city', 'address.city_text']]
const addressRequiredFields = ['country', ['state', 'state_text'], 'zip_code', ['city', 'city_text'], 'street']
const excludedReadOnlyFields = ['paid', 'preparatory_user', 'sale_department', 'sdi_convention_type', 'stamp_vat_type_ref', 'stamp_amount',
                                'stamp_paid_by_customer', 'document_date', 'document_number', 'document_type', 'billing_reason', 'billing_rate', 'billing_base',
                                'delivery_address.name', 'delivery_address.phone', 'delivery_address.mobile', 'delivery_address.email', 'delivery_address.address.zip_code', 'delivery_address.address.street',
                                'transport_date', 'transport_package_num', 'transport_weight', 'packaging_type_ref', 'shipping_cost_type', 'transport_type', 'carrier_ref']

const {
  gridRef,
  refreshGridAsync,
  focusGrid,
  onFocusedRowChangedDebounced: onFocusedRowChanged,
} = dataGrid.useGridRef()
const { partialSave, onRowValidating } = usePartialSave()
const { applyFilter, setMultiSelectFilterRef, clearMultiSelectFilter } = useMultiSelectFilter()
const { onDocumentInitNewRowAsync, getDocumentDetailsAsync, onDocumentRowUpdating, getDocumentTags, onDocumentSaving, onDocumentEditorPreparing } = useDocumentIdentifiers()
const { addWarning: addSaleDocumentWarning, displayWarnings: displaySaleDocumentWarnings, checkPartialFields } = dataGrid.useGenerateDocumentWarning()
const { validationTabIndexErrors, onTabsValidating } = useValidationTabs()
const { currentCountryId, currentVatCountryId, currentStateId, currentDeliveryCountryId, currentDeliveryStateId, currentZipCode, currentDeliveryZipCode, currentFilteredCitiesCount, currentDeliveryFilteredCitiesCount, setCellValueCity, setCellValueZipCode, onCountryChanged, onStateChanged } = useAddressRef({ onVatCountryChanged: () => updateFormLabels(editors, subjectLabelFields) })
const currentCustomerVatCountryCode = computed(() => {
  const country = webSiteStore.countries.find(x => x.id === currentVatCountryId.value)
  return country ? country.iso3_code : null
})
const { addWarning: addDocumentWarning, displayWarnings: displayGenerateDocumentWarnings } = dataGrid.useGenerateDocumentWarning()
const { isVatCountryIta, isNameExtMandatory } = useSubjectService()

const { repaintRow } = dataGrid.useUpdateMasterDetail()
const onDetailRowSaved = async(parentId, removedRow) => {
  clearRemovedRoundingRowFlag(selectedDocument.value, removedRow)
  const updatedDocument = await repaintRow(gridRef, saleStore.api.getDocumentByIdAsync, parentId, parentId)
  selectedDocument.value = updatedDocument
  isReadOnly.value = isSaleDocumentLocked(updatedDocument) || !!updatedDocument?.has_rounding_expense_row || !!updatedDocument?.has_rounding_row
}

const allComputedTags = computed(() => {
  return getDocumentTags(isPaid, includesVat)
})

const isElectronicInvoiceLocked = (saleDocument) => !!saleDocument?.electronic_invoice_progress_status && !portalStore.electronicInvoiceEditableFamilies.includes(portalStore.getElectronicInvoiceFamilyByProgressStatus(saleDocument.electronic_invoice_progress_status))
const isSaleDocumentLocked = (saleDocument) => saleDocument?.has_sale_document || isElectronicInvoiceLocked(saleDocument)

const getSaleDocumentLockedReasons = (saleDocument) => {
  const reasons = []
  if (saleDocument?.has_rounding_row) reasons.push(i18n.t('common.documentLockedReasonType.rounding_row'))
  if (saleDocument?.has_rounding_expense_row) reasons.push(i18n.t('common.documentLockedReasonType.rounding_expense_row'))
  if (saleDocument?.has_sale_document) reasons.push(i18n.t('common.documentLockedReasonType.generated_document'))
  if (isElectronicInvoiceLocked(saleDocument)) reasons.push(i18n.t('common.documentLockedReasonType.electronic_invoice_sent'))
  return reasons
}

const entityLock = useEntityLock({
  baseLockUrl: saleApi.getAbsoluteUrl() + saleApi.documentsUrl,
  modelName: 'SaleDocument',
  gridRef,
})
const rowButtons = entityLock.getEntityLockButtons({
  visible: {
    edit: (e) => !e.isReadonly && !(e.data?.has_rounding_expense_row || e.data?.has_rounding_row) && !entityLock.isLocked(false, e.entityId),
    delete: (e) => !e.isReadonly && !(e.data?.has_rounding_expense_row || e.data?.has_rounding_row) && !entityLock.isLocked(false, e.entityId),
    view: (e) => e.isReadonly || e.data?.has_rounding_expense_row || e.data?.has_rounding_row || entityLock.isLocked(false, e.entityId)
  }
})
rowButtons.push(
  {
    name: 'locked',
    text: i18n.t('common.documentLocked'),
    icon: 'mdi-lock',
    class: ['dx-link', 'dx-icon', 'mdi', 'mdi-lock', 'dx-link-icon'],
    tooltipTitle: i18n.t('common.documentLocked'),
    tooltipFn: (e) => getSaleDocumentLockedReasons(e.data),
    visibleFn: (e) => getSaleDocumentLockedReasons(e.data).length > 0,
    onClick: () => {}
  }
)

const onInitializedCustom = (e) => {
  if (isReadOnly.value && !dataGrid.isAdding.value) {
    const toolbarItems = e.component.option('toolbarItems') || []

    const updatedToolbarItems = toolbarItems.map((item, index) => {
      if (index === toolbarItems.length - 1) return item
      return {
        ...item,
        visible: false,
      }
    })

    e.component.option('toolbarItems', updatedToolbarItems)
  } else {
    entityLock.readOnlyToolbarItems(e)
  }
}

const onDetailContentReady = (e) => {
  gridRef.value.instance.updateDimensions()
}

const updateTabPanelVisibility = () => {
  if (editingTabPanelInstance.value) {
    editingTabPanelInstance.value.option('items[4].visible', (!!currentSaleDocumentType.value & (currentSaleDocumentType.value === portalStore.documentTypesEnum.SALE_DDT || currentSaleDocumentType.value === portalStore.documentTypesEnum.SALE_IMMEDIATE_INVOICE)))
  }
}

const formRepaintTabHandler = () => {
  const activeTabIndex = formInstance.value.getEditor('tabbed').option('selectedIndex')
  formInstance.value?.repaint()
  formInstance.value.getEditor('tabbed').option('selectedIndex', activeTabIndex)
}

const { customizeItem, updateFormLabels, registerLabelEditors } = useLabelController({ vehicleFuelType: currentVehicleFuelType, subjectType: currentCustomerTypeId, subjectSubTypeId: currentCustomerSubTypeId, vatCountryId: currentVatCountryId })
const subjectLabelFields = ['customer.name', 'customer.name_ext', 'customer.subject_sub_type_ref', 'customer.vat_number', 'customer.tax_code']

const getEditColumns = (subjectSubType, documentPageType) => {
  return [
    {
      itemType: 'group',
      items: [
        {
          itemType: 'group',
          caption: i18n.t('common.data', 2),
          name: 'mdi-cash',
          captionTemplate: 'generic-caption-template',
          colCount: 4,
          items: [
            { dataField: 'date', colSpan: 2 },
            { dataField: 'series', editorOptions: { readOnly: true } },
            { dataField: 'number_mnemonic' },
            { dataField: 'sale_document_type', colSpan: 4 },
            { dataField: 'sale_reason_ref', colSpan: 4 },
            { dataField: 'deposit', colSpan: 2 },
            { dataField: 'sale_department', colSpan: 2 },
            { dataField: 'preparatory_user', colSpan: 2 },
            { dataField: 'urgency', cssClass: 'overflow-visible checkbox-padding-top', colSpan: 2 },
            { dataField: 'payment_type_ref', colSpan: 2 },
            { dataField: 'includes_vat', colSpan: 2 },
            { dataField: 'discount', colSpan: 2, editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions() } },
            { dataField: 'charge_percentage', colSpan: 2, editorOptions: dataGrid.usePercentageOptions(), cssClass: 'overflow-visible' },
            { dataField: 'paid', cssClass: 'checkbox-padding-top', colSpan: 2 },
          ]
        },
        {
          itemType: 'group',
          caption: i18n.t('common.documentStatus'),
          name: 'mdi-wrench',
          captionTemplate: 'generic-caption-template',
          colCount: 4,
          items: [
            {
              dataField: 'tags',
              colSpan: 4,
              tagTemplate: 'tagTemplate'
            }
          ]
        }
      ]
    },
    {
      itemType: 'tabbed',
      colSpan: 3,
      name: 'tabbed',
      tabPanelOptions: {
        onInitialized: (e) => { editingTabPanelInstance.value = e.component },
        onContentReady: updateTabPanelVisibility,
        deferRendering: false,
      },
      tabs: [
        {
          title: i18n.t('common.mainInfo'),
          tabTemplate: 'validationTabTemplate',
          colCount: 3,
          tabIndex: 0,
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.customerData'),
              name: 'mdi-account-circle',
              captionTemplate: 'customer-info-caption-template',
              validationGroup: 'ownerGroup',
              colSpan: 3,
              colCount: 4,
              items: [
                { dataField: 'customer_id', editorOptions: { tabIndex: 0 } },
                { dataField: 'customer.subject_type_ref', editorOptions: { tabIndex: 0 }},
                { dataField: 'customer.subject_sub_type_ref', editorOptions: { tabIndex: 0 }},
                { itemType: 'empty', editorOptions: { tabIndex: 0 } },
                { dataField: 'customer.name', label: { text: i18n.t('common.nameAlt') }, editorOptions: { tabIndex: 0 } },
                { dataField: 'customer.name_ext', label: { text: i18n.t('common.nameExtAlt') }, editorOptions: { tabIndex: 0 } },
                { itemType: 'empty', colSpan: 2, editorOptions: { tabIndex: 0 } },
                ...dataGrid.useDefaultAddressEditing('customer.address', 1, null, {}, true),
                { dataField: 'customer.email', label: { text: i18n.t('common.email') }, editorOptions: { tabIndex: 0 } },
                {
                  dataField: 'customer.mobile',
                  label: { text: i18n.t('common.mobile') },
                  editorOptions: {
                    tabIndex: 0,
                    onContentReady: (e) => {
                      dataGrid.usePhoneMask(e.element, { definitions: dataGrid.useMask().mobileMaskRules })
                    }
                  }
                },
                { dataField: 'customer.vat_country_id', label: { text: i18n.t('common.vatCountry')}, editorOptions: { tabIndex: 0 } },
                { dataField: 'customer.vat_number', label: { text: i18n.t('common.vatNumber')}, editorOptions: { tabIndex: 0 } },
                { dataField: 'customer.tax_code', label: { text: i18n.t('common.taxCode') }, editorOptions: { tabIndex: 0 } },
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.deliveryAddress'),
              name: 'mdi-truck',
              captionTemplate: 'delivery-address-info-caption-template',
              colSpan: 2,
              colCount: 3,
              items: [
                { dataField: 'delivery_address_ref', editorOptions: { tabIndex: 0 } },
                { dataField: 'delivery_address.name', label: { text: i18n.t('common.nameAlt') }, editorOptions: { tabIndex: 0 } },
                { itemType: 'empty', editorOptions: { tabIndex: 0 } },
                ...dataGrid.useDefaultAddressEditing('delivery_address.address', 1, null, {}, true),
                { dataField: 'delivery_address.email', label: { text: i18n.t('common.email') }, editorOptions: { tabIndex: 0 } },
                {
                  dataField: 'delivery_address.mobile',
                  label: { text: i18n.t('common.mobile') },
                  editorOptions: {
                    tabIndex: 0,
                    onContentReady: (e) => {
                      dataGrid.usePhoneMask(e.element, { definitions: dataGrid.useMask().mobileMaskRules })
                    }
                  }
                },
                {
                  dataField: 'delivery_address.phone',
                  label: { text: i18n.t('common.phone') },
                  editorOptions: {
                    tabIndex: 0,
                    onContentReady: (e) => {
                      dataGrid.usePhoneMask(e.element, { definitions: dataGrid.useMask().mobileMaskRules })
                    }
                  }
                },
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.documentNote', 2),
              name: 'mdi-notebook-edit',
              captionTemplate: 'generic-caption-template',
              items: [
                {
                  dataField: 'notes',
                  editorType: 'dxTextArea',
                  editorOptions: { height: '13rem', tabIndex: 0 }
                },
              ]
            },
          ]
        },
        {
          title: i18n.t('common.billing'),
          tabTemplate: 'validationTabTemplate',
          tabIndex: 1,
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.invoiceToCustomer'),
              captionTemplate: 'customer-info-caption-template',
              colSpan: 3,
              colCount: 4,
              visible: documentPageType !== 'invoice',
              items: [
                { dataField: 'billing_customer_id', editorOptions: { validationTabIndex: 1 } },
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.fiscalData'),
              name: 'mdi-receipt-text-outline',
              captionTemplate: 'generic-caption-template',
              colSpan: 3,
              colCount: 4,
              items: [
                { dataField: 'vat_type_ref', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'currency_ref', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'exchange_rate', editorOptions: { readOnly: true, validationTabIndex: 1 } },
                { dataField: 'exchange_rate_date', editorOptions: { readOnly: true, validationTabIndex: 1 } },
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.expense', 2),
              colSpan: 3,
              colCount: 4,
              items: [
                { dataField: 'stamp_paid_by_customer', editorOptions: { validationTabIndex: 1 } },
                { dataField: 'stamp_amount', editorOptions: { ...dataGrid.useCurrencyOptions(), min: 0, validationTabIndex: 1 } },
                { dataField: 'stamp_vat_type_ref', editorOptions: { validationTabIndex: 1, showClearButton: true} },
              ]
            },
            {
              itemType: 'group',
              caption: i18n.t('common.withholdingTax'),
              colSpan: 3,
              colCount: 4,
              items: [
                { dataField: 'billing_base', editorOptions: { ...dataGrid.useCurrencyOptions({ min: 0 }), validationTabIndex: 1 } },
                { dataField: 'billing_rate', editorOptions: { ...dataGrid.usePercentageOptions(), validationTabIndex: 1 } },
                { dataField: 'billing_amount', editorOptions: { ...dataGrid.useCurrencyOptions({ min: 0 }), validationTabIndex: 1 } },
                { dataField: 'billing_reason', editorOptions: { hint: i18n.t('common.paymentTypeReason770s'), validationTabIndex: 1 } },
              ]
            },
          ]
        },
        {
          title: i18n.t('common.vehicle'),
          tabTemplate: 'validationTabTemplate',
          tabIndex: 2,
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.vehicleInfo'),
              colSpan: 3,
              colCount: 8,
              items: [
                { dataField: 'vehicle_id', colSpan: 2, editorOptions: { tabIndex: 2 } },
                { dataField: 'vehicle.supplier_brand_ref', colSpan: 2, editorOptions: { readOnly: true, tabIndex: 2 } },
                { dataField: 'vehicle.plate_number', colSpan: 2, editorOptions: { readOnly: true, tabIndex: 2 } },
                { dataField: 'vehicle.vin', editorOptions: { readOnly: true, maxLength: 17, tabIndex: 2 }, colSpan: 2 },
                { dataField: 'vehicle.name', colSpan: 2, editorOptions: { readOnly: true, tabIndex: 2 } },
                { dataField: 'vehicle.color', colSpan: 2, editorOptions: { readOnly: true, tabIndex: 2 } },
                { dataField: 'vehicle.km', editorOptions: { readOnly: true, min: 0, tabIndex: 2 } },
                { dataField: 'vehicle.engine_size', editorOptions: { readOnly: true, min: 0, tabIndex: 2 } },
                { dataField: 'vehicle.engine_code', colSpan: 2, editorOptions: { readOnly: true, tabIndex: 2 } },
              ]
            },
          ]
        },
        {
          title: i18n.t('common.sendingSdi'),
          tabTemplate: 'validationTabTemplate',
          tabIndex: 3,
          items: [
            subjectSubType === subjectTypePARef ? {
              itemType: 'group',
              caption: i18n.t('common.authorizationData', 2),
              colCount: 4,
              items: [
                { dataField: 'sdi_convention_type', colSpan: 2 },
                { itemType: 'empty', colSpan: 2 },
                { dataField: 'sdi_authorization_type', colSpan: 2 },
                { dataField: 'sdi_order_date', colSpan: 2 },
                { dataField: 'sdi_purchase_order_number', colSpan: 2 },
                { dataField: 'sdi_purchase_order_line', colSpan: 2 },
              ]
            } : {
              itemType: 'group',
              caption: i18n.t('common.authorizationData', 2),
              colCount: 4,
              items: [{ dataField: 'sdi_convention_type', colSpan: 2 }]
            },
            subjectSubType === subjectTypePARef ? {
              itemType: 'group',
              caption: i18n.t('common.reference', 2),
              colCount: 4,
              items: [
                { dataField: 'sdi_cig', colSpan: 2 },
                { dataField: 'sdi_cup', colSpan: 2 },
              ]
            } : { itemType: 'empty' },
            {
              itemType: 'group',
              caption: i18n.t('common.reference', 2),
              colCount: 4,
              items: [{ dataField: 'sdi_document_type', colSpan: 2, editorOptions: { readOnly: true } }]
            }
          ]
        },
        {
          title: i18n.t('common.transportData'),
          tabTemplate: 'validationTabTemplate',
          tabIndex: 4,
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.transportData'),
              colSpan: 3,
              colCount: 8,
              items: [
                { dataField: 'transport_date', colSpan: 2, dataType: 'datetime', editorOptions: { tabIndex: 4 }},
                { itemType: 'empty', colSpan: 6, editorOptions: { tabIndex: 4 } },
                { dataField: 'transport_package_num', colSpan: 2, editorOptions: { tabIndex: 4 }},
                { dataField: 'transport_weight', colSpan: 2, editorOptions: { tabIndex: 4 }},
                { dataField: 'packaging_type_ref', colSpan: 2, editorOptions: { tabIndex: 4 }},
                { dataField: 'shipping_cost_type', colSpan: 2, editorOptions: { tabIndex: 4 } },
                { dataField: 'transport_type', colSpan: 2, editorOptions: { tabIndex: 4 } },
                { dataField: 'carrier_ref', colSpan: 2, editorOptions: { tabIndex: 4 } },
              ]
            }
          ]
        },
        {
          title: i18n.t('common.additionalData'),
          tabTemplate: 'validationTabTemplate',
          tabIndex: 5,
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.refDocument'),
              name: 'mdi-file-document-multiple-outline',
              captionTemplate: 'generic-caption-template',
              colSpan: 3,
              colCount: 4,
              items: [
                { dataField: 'document_type', label: { text: i18n.t('common.type') }, editorOptions: { validationTabIndex: 5 } },
                { dataField: 'document_number', label: { text: i18n.t('common.number') }, editorOptions: { validationTabIndex: 5 } },
                { dataField: 'document_date', label: { text: i18n.t('common.date') }, editorOptions: { validationTabIndex: 5 } },
              ]
            },
          ]
        },
      ]
    }
  ]
}

const columns = useColumns(
  editors,
  gridRef,
  currentCustomer,
  currentSaleDocumentType,
  currentCountryId,
  currentStateId,
  currentDeliveryCountryId,
  currentDeliveryStateId,
  currentCustomerTypeId,
  currentCustomerSubTypeId,
  currentCarService,
  currentSaleDocumentPage,
  isBillingCustomerDisabled,
  currentVehicleFuelType,
  updateTabPanelVisibility,
  formRepaintTabHandler,
  getEditColumns,
  includesVat,
  isPaid,
  currentVatCountryId,
  currentCustomerVatCountryCode,
  setCellValueZipCode,
  setCellValueCity
)

const editing = dataGrid.useDefaultEditing(useDialogTitle().dialogTitle, {
  colCount: 4,
  items: getEditColumns(currentCustomer.value?.subject_sub_type_ref, currentSaleDocumentPage.value),
  onInitialized: (e) => { formInstance.value = e.component },
  customizeItem: customizeItem,
}, {
  allowAdding: !props.selectMode,
  allowUpdating: (e) => !props.selectMode && !(e.data?.has_rounding_row || e.data?.has_rounding_expense_row),
  allowDeleting: (e) => !props.selectMode && !(e.data?.has_rounding_row || e.data?.has_rounding_expense_row),
  onInitializedCustom: onInitializedCustom,
  entityLock: entityLock,
})

const generatingDocumentMap = computed(() => {
  const row = selectedRows.value?.[0]
  if (!row) return null

  const map = {
    [portalStore.documentTypesEnum.SALE_DDT]: portalStore.documentGeneratingEnum.DDT,
    [portalStore.documentTypesEnum.SALE_IMMEDIATE_INVOICE]: portalStore.documentGeneratingEnum.IMMEDIATE_INVOICE,
    [portalStore.documentTypesEnum.SALE_INVOICE]: portalStore.documentGeneratingEnum.DEFERRED_INVOICE,
  }

  return map[row.sale_document_type] ?? null
})

const onInitNewRow = async(e) => {
  isReloadCustomerDataAllowed.value = false
  isReloadDeliveryAddressDataAllowed.value = false
  validationTabIndexErrors.value = []
  e.component.instance().option('editing.form.items[0].items[0].caption', i18n.t('common.data', 2))

  e.data.date = new Date()
  e.data.transport_date = new Date().toISOString()
  currentCustomer.value = null
  isPaid.value = e.data?.paid
  currentVehicleFuelType.value = null

  if (registryStore.companyData.default_sale_document_deposit) {
    e.data.deposit = registryStore.companyData.default_sale_document_deposit
  }
  if (registryStore.companyData.default_sale_document_payment_type_ref) {
    e.data.payment_type_ref = registryStore.companyData.default_sale_document_payment_type_ref
  }
  if (registryStore.companyData.default_sale_document_stamp_vat_type_ref) {
    e.data.stamp_vat_type_ref = registryStore.companyData.default_sale_document_stamp_vat_type_ref
  }
  if (registryStore.companyData.default_sale_document_sale_reason_ref) {
    e.data.sale_reason_ref = registryStore.companyData.default_sale_document_sale_reason_ref
  }
  if (registryStore.companyData.default_currency_ref) {
    e.data.currency_ref = registryStore.companyData.default_currency_ref

    const currentCurrency = webSiteStore.currencies.find((currency) => currency.id === registryStore.companyData.default_currency_ref)
    if (currentCurrency) {
      e.data.exchange_rate = currentCurrency.exchange_rate
      e.data.exchange_rate_date = currentCurrency.exchange_rate_date
    }
  }

  e.data.sale_department = portalStore.saleDepartments.find(sd => sd.code === webSiteStore.saleDepartmentCodeEnum.SHOP)?.id
  const user_detail = webSiteStore.userDetails.find(ud => ud.user_id === webSiteStore.user.id)
  if (user_detail?.sale_department) {
    e.data.sale_department = user_detail.sale_department
  }

  currentSaleDocumentType.value = e.data.sale_document_type = e.data.document_type = null
  isBillingCustomerDisabled.value = !(currentSaleDocumentType.value === portalStore.documentTypesEnum.SALE_CORRESPONDENT || currentSaleDocumentType.value === portalStore.documentTypesEnum.SALE_DDT)

  setSdiDocumentTypeValue(e.data, e.data)
  e.promise = (async() => { await onDocumentInitNewRowAsync(e) })()
  e.data.includes_vat = includesVat.value = e.data.includes_vat ?? false
}

const onDocumentGenerated = async() => {
  selectedDocument.value = await repaintRow(gridRef, saleStore.api.getDocumentByIdAsync, selectedDocument.value?.id, selectedDocument.value?.id)
  gridRef.value?.instance?.clearSelection()
  await refreshGridAsync()
}

const onRowInserting = (e) => {
  if (e.data?.customer?.id) {
    delete e.data.customer.code
  }

  const { transport_date } = e.data
  e.data = {
    ...e.data,
    transport_date: transport_date
  }
}

const handleWarningMessages = (err) => {
  if (err?.response?.data?.warnings) {
    err?.response?.data?.warnings.forEach(i => {
      i.messages.forEach(m => {
        addSaleDocumentWarning(i.document, i18n.t(m))
      })
      displaySaleDocumentWarnings()
    })
  } else if (err?.response?.data?.message) {
    notifyError(err.response.data.message)
  }
}

const onToolbarPreparing = (e) => {
  defaultToolbar = dataGrid.useDefaultToolbar(e, {
    allowBatchDelete: true,
    addButtonOptions: { hint: `${i18n.t('common.add')} ${i18n.t('common.document')}` }
  })

  e.toolbarOptions.items.unshift(
    {
      location: 'after',
      widget: 'dxDropDownButton',
      options: {
        icon: 'textdocument',
        text: `${i18n.t('common.generate')}...`,
        disabled: true,
        useSelectMode: false,
        displayExpr: 'text',
        dropDownOptions: { width: '20rem' },
        onItemClick: async(e) => {
          if (selectedRows.value && selectedRows.value[0]) {
            generateDocumentType.value = e.itemData.id
            const generatingItem = (await (saleApi.getDocumentByIdAsync(selectedRows.value[0].id)))?.data
            if (!generatingItem) {
              notifyError(i18n.t('error.item_not_found'))
              return
            }

            const docName = `${i18n.t('common.saleDocumentAlt')} ${generatingItem.series} - ${generatingItem.number_mnemonic}`

            if (selectedRows.value.some(row => row.has_sale_document)) { addDocumentWarning(docName, i18n.t('error.validation.related_document_already_exists')) }
            if (selectedRows.value.some(row => row.rows_count === 0)) { addDocumentWarning(docName, i18n.t('error.validation.no_related_rows_found')) }

            await checkPartialFields(generateDocumentType.value, docName, selectedRows.value, addDocumentWarning)

            if (!displayGenerateDocumentWarnings()) {
              refDocument.value = structuredClone(generatingItem)
              refDocument.value.generating_document_date = refDocument.value?.date
              refDocument.value.sale_document_number_mnemonic = ''
              refDocument.value.invoicing_customer = registryStore.customers.find(rc => rc.id === refDocument.value.billing_customer_id) ?? null

              if (generateDocumentType.value === portalStore.documentGenerateEnum.DEFERRED_INVOICE) {
                refDocument.value.sale_document_type = portalStore.documentTypesEnum.SALE_INVOICE
              }
              if (generateDocumentType.value === portalStore.documentGenerateEnum.CREDIT_NOTE) {
                refDocument.value.sale_document_type = portalStore.documentTypesEnum.SALE_CREDIT_NOTE
              }
              generateDialogVisible.value = true
            }
          }
        },
        items: [
          { id: portalStore.documentGenerateEnum.DEFERRED_INVOICE, text: i18n.t('common.documentType.sale_deferred_invoice'), icon: 'mdi mdi-receipt-text-outline' },
          { id: portalStore.documentGenerateEnum.CREDIT_NOTE, text: i18n.t('common.documentType.sale_credit_note'), icon: 'mdi mdi-receipt-text-outline' },
        ],
        onInitialized: (e) => { generateDocumentToolbarButton = e.component }
      },
    },
    print.printToolbarWidget({ onClick: async() => {
      try {
        const filename = getLocalizedFileName()
        await saleStore.api.printSaleDocumentsAsync(filename, {filename: filename, ids: selectedRows.value.map(i => i.id), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone}, { skipErrorMessage: (e) => (e.response?.data?.warnings) })
      } catch (err) {
        handleWarningMessages(err)
      }
    }}),
    print.printOptionsToolbarWidget()
  )

  e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem)
}

const getLocalizedFileName = () => {
  return selectedRows.value.length === 1 ? i18n.t('common.saleDocument') + '.pdf' : i18n.t('common.saleDocument', 2) + '.zip'
}

const onRowUpdating = (e) => {
  if (e.newData?.customer?.id) {
    delete e.newData.customer.code
  }
  if (e.newData?.customer && !e.newData.customer.id) {
    e.newData.customer.id = e.oldData.customer.id
  }
  onDocumentRowUpdating(e, selectedDocument)
}

const onClearFilter = () => {
  clearMultiSelectFilter()
  router.replace()
}

const isSaveDisabled = (key, requiredFields, addressFields = []) => {
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, key) => acc?.[key], obj)
  }

  const isFieldGroupValid = (obj, field) => {
    if (Array.isArray(field)) {
      return field.some(f => !!getNestedValue(obj, f))
    }
    return !!getNestedValue(obj, field)
  }

  const grid = gridRef.value?.instance
  if (!grid) return true

  const editingRow = grid.getVisibleRows().find(row => row.isEditing)
  if (!editingRow) return true

  const obj = editingRow.data?.[key]
  if (!obj) return true

  if (!!obj.id || (key === 'delivery_address' && !!editingRow.data?.delivery_address_ref)) return true

  if (requiredFields.some(f => !isFieldGroupValid(obj, f))) {
    return true
  }

  if (key === 'customer' && isNameExtMandatory(obj.subject_type_ref) && !obj.name_ext) return true

  if (addressFields.length > 0) {
    const hasSomeExtra = addressFields.some(f => isFieldGroupValid(obj, f))

    if (hasSomeExtra) {
      const hasAllExtra = addressFields.every(f => isFieldGroupValid(obj, f))
      if (!hasAllExtra) return true
    }
  }

  return false
}

const onEditorPreparing = (e) => {
  if (firstEditLoad && e.row?.data?.id) {
    firstEditLoad = false
    e.promise = getDocumentDetailsAsync(e.row?.data?.id, saleStore.api.getSaleDocumentDetailsAsync, e.row.data, { onNotFound: () => e.component?.cancelEditData() }).then(res => {
      e.row.data = res.value
      currentVehicleFuelType.value = res.value?.vehicle?.fuel_type ?? null
      currentCustomerTypeId.value = res.value?.customer?.subject_type_ref ?? null
      currentCustomer.value = res.value?.customer
      isBillingCustomerDisabled.value = !(res.value?.sale_document_type === portalStore.documentTypesEnum.SALE_CORRESPONDENT || res.value?.sale_document_type === portalStore.documentTypesEnum.SALE_DDT)
      isDeliveryAddressSaveDisabled.value = !!res.value?.delivery_address_ref

      currentCountryId.value = res.value.customer?.address?.country
      currentStateId.value = res.value.customer?.address?.state
      currentDeliveryCountryId.value = res.value.delivery_address?.address?.country
      currentDeliveryStateId.value = res.value.delivery_address?.address?.state
      currentVatCountryId.value = res.value?.vat_country_id
      e.row.data.customer.vat_country_id = res.value?.vat_country_id

      gridRef.value.instance.option('editing.form.items', getEditColumns(currentCustomer.value.subject_sub_type_ref, currentSaleDocumentPage.value))
      formRepaintTabHandler()
    })
  }

  if (e.parentType === 'filterRow' && e.dataField === 'sale_reason_ref') {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      dataSource: saleStore.getLocalizedSaleReasons(),
      field: e.dataField,
    })
  }

  if (e.parentType === 'filterRow' && e.dataField === 'sale_document_type') {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      dataSource: portalStore.getLocalizedDocumentTypes().filter(i => i.page === currentSaleDocumentPage.value),
      field: e.dataField,
    })
  }

  if (e.parentType === 'filterRow' && e.dataField === 'sale_department') {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      dataSource: portalStore.getLocalizedSaleDepartments(),
      field: e.dataField,
    })
  }

  if (e.parentType === 'dataRow' && (e.dataField === 'billing_base' || e.dataField === 'billing_rate' || e.dataField === 'billing_reason')) {
    e.editorOptions.onInitialized = (args) => { editors[e.dataField] = args.component }
    e.editorOptions.disabled = !(e.row?.data?.sale_document_type === portalStore.documentTypesEnum.SALE_INVOICE || e.row?.data?.sale_document_type === portalStore.documentTypesEnum.SALE_IMMEDIATE_INVOICE)
  }
  if (e.parentType === 'dataRow' && e.dataField === 'customer.vat_number') {
    e.editorOptions.onInitialized = (e) => { editors['customer.vat_number'] = e.component }
    e.editorOptions.disabled = !isVatCountryIta(currentVatCountryId.value) || e.row?.data?.id
  }
  if (e.parentType === 'dataRow' && e.dataField === 'customer.tax_code') {
    e.editorOptions.onInitialized = (e) => { editors['customer.tax_code'] = e.component }
  }
  if (e.parentType === 'dataRow' && (e.dataField === 'customer.name' || e.dataField === 'customer.name_ext' || e.dataField === 'customer.subject_type_ref' || e.dataField === 'customer.subject_sub_type_ref' ||
      e.dataField === 'customer.vat_country_id' || e.dataField === 'customer.vat_number' || e.dataField === 'customer.address.zip_code' || e.dataField === 'customer.address.street' || e.dataField === 'customer.address.state'
  )) {
    e.editorOptions.readOnly = e.row.data?.customer_id
  }
  if (e.parentType === 'dataRow' && (e.dataField === 'delivery_address.name' || e.dataField === 'delivery_address.email' || e.dataField === 'delivery_address.mobile' ||
      e.dataField === 'delivery_address.phone' || e.dataField === 'delivery_address.address.zip_code' || e.dataField === 'delivery_address.address.street' || e.dataField === 'delivery_address.address.state'
  )) {
    e.editorOptions.onInitialized = (args) => { editors[e.dataField] = args.component }
    e.editorOptions.readOnly = !e.row.data?.customer_id || !!e.row.data?.delivery_address_ref
  }
  if (e.parentType === 'dataRow' && (e.dataField === 'customer.subject_type_ref' || e.dataField === 'customer.subject_sub_type_ref' || e.dataField === 'customer.name' || e.dataField === 'customer.name_ext' || e.dataField === 'customer.mobile' ||
                                      e.dataField === 'customer.address.zip_code' || e.dataField === 'customer.address.street' || e.dataField === 'customer.address.state' ||
                                        e.dataField === 'customer.address.country' || e.dataField === 'customer.address.city' || e.dataField === 'customer.tax_code' || e.dataField === 'customer.vat_number')) {
    isCustomerSaveDisabled.value = isReadOnly.value || isSaveDisabled('customer', disabledCustomerFields, addressRequiredFields)
    const defaultValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      isCustomerSaveDisabled.value = isReadOnly.value || isSaveDisabled('customer', disabledCustomerFields, addressRequiredFields)
      updateFormLabels(editors, subjectLabelFields)
    }
  }
  if (e.parentType === 'dataRow' && (e.dataField === 'delivery_address.name' || e.dataField === 'delivery_address.address.zip_code' ||
      e.dataField === 'delivery_address.address.street' || e.dataField === 'delivery_address.address.state' || e.dataField === 'delivery_address.address.country' || e.dataField === 'delivery_address.address.city'
  )) {
    isDeliveryAddressSaveDisabled.value = isReadOnly.value || isSaveDisabled('delivery_address', disabledDeliveryAddressFields, addressRequiredFields)
    const defaultValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      isDeliveryAddressSaveDisabled.value = isReadOnly.value || isSaveDisabled('delivery_address', disabledDeliveryAddressFields, addressRequiredFields)
    }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'customer.subject_type_ref') {
    const defaultValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (args) => {
      currentCustomerTypeId.value = args.value
      currentCustomerSubTypeId.value = null
      defaultValueChanged(args)
      updateFormLabels(editors, subjectLabelFields)
    }
  }
  registerLabelEditors(e, editors, ['customer.name', 'customer.name_ext', 'customer.subject_sub_type_ref'])

  onDocumentEditorPreparing(e, editors, saleStore.api.getSaleDocumentNextIdByDataAsync, gridRef, excludedReadOnlyFields)

  if (isReadOnly.value && e.parentType === 'dataRow' || isSaleDocumentLocked(e.row?.data)) {
    e.editorOptions.readOnly = true
  }
  if (e.parentType === 'dataRow' && e.dataField === 'sale_document_type' && e.row?.data?.id) {
    e.editorOptions.readOnly = true
  }
}

const getFilterDataSource = (field) => {
  const buildFilterOptions = (items) => [{
    items: [...items]
  }]

  switch (field) {
    case 'sale_reason_ref':
      return buildFilterOptions(saleStore.getLocalizedSaleReasons())
    case 'sale_department':
      return buildFilterOptions(portalStore.getLocalizedSaleDepartments())
    case 'sale_document_type':
      return buildFilterOptions(portalStore.getLocalizedDocumentTypes().filter(i => i.page === currentSaleDocumentPage.value))
    default:
      return []
  }
}

const onSplitterResize = (e, splitterId) => {
  const totalSize = e.component.option('orientation') === 'vertical'
    ? e.component.element().offsetHeight
    : e.component.element().offsetWidth

  const sizes = e.component.option('items').map(item => {
    const px = parseFloat(item.size)
    const percentage = ((px / totalSize) * 100).toFixed(2) + '%'
    return { size: percentage }
  })
  webSiteStore.setSplitterState(splitterId, sizes)

  if (splitterId === mainSplitterId && selectedDocument.value && gridRef.value?.instance) {
    const gridInstance = gridRef.value.instance

    gridInstance.updateDimensions()
    gridInstance.repaint()

    const rowIndex = gridInstance.getRowIndexByKey(selectedDocument.value.id)
    if (rowIndex >= 0) {
      const rowHeight = gridInstance.option('rowHeight') || 37
      const scrollTop = rowIndex * rowHeight

      const scrollable = gridInstance.getScrollable()
      if (scrollable) {
        const currentScroll = scrollable.scrollOffset()
        scrollable.scrollTo({ x: currentScroll.left, y: scrollTop })
        gridInstance.repaint()
      }
    }
  }
}

const restoreSplitterSize = (e, splitterId) => {
  const saved = webSiteStore.splitterStates[splitterId]
  if (saved) {
    try {
      saved.forEach((item, index) => {
        e.component.option(`items[${index}].size`, item.size)
      })
    } catch (err) {
      console.warn(`Splitter "${splitterId}": data error.`)
    }
  }
}

const onEditCanceled = (e) => {
  isReadOnly.value = false
}

const onSelectionChanged = async(e) => {
  selectedRows.value = e.selectedRowsData
  defaultToolbar?.batchDeleteToolbar?.option('disabled', e.selectedRowsData.length === 0)
  generateDocumentToolbarButton.option('disabled', e.selectedRowsData.length !== 1 || e.selectedRowsData?.some(i => entityLock.isLocked(false, i.id)))

  const firstSelected = e.selectedRowsData[0]
  if (firstSelected) {
    const type = firstSelected.sale_document_type
    const generableSourceTypes = [portalStore.documentTypesEnum.SALE_DDT, portalStore.documentTypesEnum.SALE_IMMEDIATE_INVOICE, portalStore.documentTypesEnum.SALE_INVOICE]
    const canGenerateDocument = !firstSelected.has_sale_document && generableSourceTypes.includes(type)
    if (canGenerateDocument) {
      const updatedItems = generateDocumentToolbarButton.option('items').map(item => {
        if (type === portalStore.documentTypesEnum.SALE_DDT) {
          return {
            ...item,
            visible: item.id === portalStore.documentGenerateEnum.DEFERRED_INVOICE
          }
        }

        if ([portalStore.documentTypesEnum.SALE_IMMEDIATE_INVOICE, portalStore.documentTypesEnum.SALE_INVOICE].includes(type)) {
          return {
            ...item,
            visible: item.id === portalStore.documentGenerateEnum.CREDIT_NOTE
          }
        }

        return { ...item, visible: false }
      })
      generateDocumentToolbarButton.option('items', updatedItems)
    } else {
      generateDocumentToolbarButton.option('disabled', true)
    }
  }
  print.printToolbarButton.value.option('disabled', e.selectedRowsData.length === 0)
  print.printOptionsToolbarButton.value.option('disabled', e.selectedRowsData.length === 0)
}

const onFocusedRowChangedDebounced = async(e) => onFocusedRowChanged(e, selectedDocument)

const batchDeleteAsync = async(selectedIds) => {
  await saleStore.api.batchDeleteSaleDocumentAsync(selectedIds)
  selectedDocument.value = null
}

const onSaving = (e) => {
  const delivery = e.changes?.[0]?.data?.delivery_address

  if ([portalStore.documentTypesEnum.SALE_INVOICE, portalStore.documentTypesEnum.SALE_IMMEDIATE_INVOICE].includes(e.changes?.[0]?.data?.sale_document_type) && e.changes?.[0]?.type === 'insert') {
    e.promise = (async() => {
      const invoiceToCustomerId = registryStore.customers.find(c => c.id === e.changes?.[0].data?.customer_id)?.invoice_to_customer
      if (invoiceToCustomerId) {
        const invoiceToCustomer = registryStore.customers.find(c => c.id === invoiceToCustomerId)
        const changeCustomer = await customDialog(i18n.t('common.invoiceToCustomer'), i18n.t('common.invoiceToCustomerText', { invoice_to_customer_name: `${invoiceToCustomer?.name} ${invoiceToCustomer?.name_ext}` }), i18n.t('common.yes'), i18n.t('common.no'))
        if (changeCustomer) {
          e.changes[0].data.customer_id = invoiceToCustomerId
          e.changes[0].data.customer = invoiceToCustomer
        }
      }
    })()
  }

  if (delivery && !delivery.id) {
    delete e.changes[0].data.delivery_address
  }

  if (hasRows.value) {
    delete e.changes?.[0]?.data?.includes_vat
  }

  onDocumentSaving(e, selectedDocument, { isOwnerSaveDisabled: isCustomerSaveDisabled, requireCustomerPrivacyCheck: true, check_tax_code: true })
}

const onSaved = async(e) => { await refreshGridAsync() }

const reloadCustomer = async() => {
  genericDialog(
    null,
    i18n.t('common.reloadCustomerInfo'),
    { cancel: i18n.t('common.no'), ok: i18n.t('common.yes'), persistent: true }
  ).onOk(async() => {
    const grid = gridRef.value?.instance
    if (!grid) return

    const editingRow = grid.getVisibleRows().find(row => row.isEditing)
    if (!editingRow) return

    const rowIndex = editingRow.rowIndex
    const res = await saleStore.api.saleDocumentReloadCustomerAsync(selectedDocument.value?.id, {})
    if (res?.data?.customer_id) {
      await registryStore.fetchCustomersAsync(true)
      grid.cellValue(rowIndex, 'customer_id', registryStore.customers.find(c => c.id === res.data.customer_id))
      formInstance.value?.repaint()
    }
  })
}

const reloadAddress = async() => {
  genericDialog(
    null,
    i18n.t('common.reloadDeliveryAddressInfo'),
    { cancel: i18n.t('common.no'), ok: i18n.t('common.yes'), persistent: true }
  ).onOk(async() => {
    const grid = gridRef.value?.instance
    if (!grid) return

    const editingRow = grid.getVisibleRows().find(row => row.isEditing)
    if (!editingRow) return

    const rowIndex = editingRow.rowIndex
    const res = await saleStore.api.saleDocumentReloadDeliveryAddressAsync(selectedDocument.value?.id, {})
    if (res?.data?.delivery_address_id) {
      await registryStore.fetchSubjectContactsAsync(true)
      grid.cellValue(rowIndex, 'delivery_address_ref', registryStore.subjectContacts.find(c => c.id === res.data.delivery_address_id))
      formInstance.value?.repaint()
    }
  })
}

const saveNewCustomer = async() => {
  const grid = gridRef.value?.instance
  if (!grid) return

  const editingRow = grid.getVisibleRows().find(row => row.isEditing)
  if (!editingRow) return

  const rowIndex = editingRow.rowIndex
  const customer = editingRow.data?.customer

  if (!customer || customer.id || !customer.subject_sub_type_ref || !customer.subject_type_ref || !customer.name) {
    grid.getController('validating')?.validate(true)
    return
  }

  const nextIdResponse = await registryStore.api.getSubjectNextIdAsync()
  customer.code = nextIdResponse.data
  customer.type = registryStore.subjectEntityTypes.CUSTOMER
  customer.customer ??= {}
  customer.customer.discount_list_ref = registryStore.getLocalizedDiscountLists()[0]?.id
  customer.vat_country_id = customer.vat_country_id
  delete customer.vat_country
  delete customer.id
  delete customer.address

  const res = await partialSave(customer, registryApi.createSubjectAsync, { grid: grid })
  if (res?.customer) {
    await registryStore.fetchCustomersAsync(true)
    grid.cellValue(rowIndex, 'customer_id', registryStore.customers.find(c => c.id === res?.customer?.id))
    formInstance.value?.repaint()
  }
}

const saveNewCustomerDeliveryAddress = async() => {
  const grid = gridRef.value?.instance
  if (!grid) return

  const editingRow = grid.getVisibleRows().find(row => row.isEditing)
  if (!editingRow) return

  const rowIndex = editingRow.rowIndex
  let customer = editingRow.data?.customer

  if (!customer?.id) {
    grid.getController('validating')?.validate(true)
    return
  }

  customer = registryStore.customers.find(i => i.id === customer.id)
  const deliveryAddress = editingRow.data?.delivery_address
  if (!deliveryAddress || deliveryAddress.id || !deliveryAddress.name || !deliveryAddress.address) {
    grid.getController('validating')?.validate(true)
    return
  }

  const nextIdResponse = await registryStore.api.getSubjectContactNextIdAsync()
  deliveryAddress.code = nextIdResponse.data
  deliveryAddress.subject_ref = -customer.subject
  deliveryAddress.is_default_delivery_address = false
  deliveryAddress.contact_type = portalStore.subjectContactTypes.DELIVERY_ADDRESS

  const res = await partialSave(deliveryAddress, registryApi.createSubjectContactAsync)
  if (res?.id) {
    deliveryAddress.id = res.id
    grid.cellValue(rowIndex, 'delivery_address_ref', deliveryAddress)
    await registryStore.fetchSubjectContactsAsync(true, { $notop: true })
    formInstance.value?.repaint()
  }

  return res
}

const onEditingStart = (e) => {
  firstEditLoad = true

  validationTabIndexErrors.value = []
  includesVat.value = e.data.includes_vat
  isPaid.value = e.data?.paid
  selectedDocument.value = e.data
  if (e.data?.sale_document_type !== null) {
    const documentTypeName = portalStore.getDocumentTypeName(e.data?.sale_document_type)
    e.component.instance().option('editing.form.items[0].items[0].caption', `${i18n.t('common.data', 2)} ${documentTypeName}`)
  }
  currentSaleDocumentType.value = e.data?.sale_document_type ?? null
  if (!e.data.sdi_document_type) {
    e.data.sdi_document_type = portalStore.getLocalizedElectronicInvoiceSdiDocumentTypes().find(i => i.code === portalStore.electronicInvoiceSdiDocumentTypeEnum.INVOICE)?.code
  }
  if (e.data?.customer?.vat_country_id) {
    currentVatCountryId.value = e.data.customer.vat_country_id
  }

  isReloadCustomerDataAllowed.value = !selectedDocument.value?.electronic_invoice_progress_status || portalStore.electronicInvoiceEditableFamilies.includes(portalStore.getElectronicInvoiceFamilyByProgressStatus(selectedDocument.value.electronic_invoice_progress_status))
  isReloadDeliveryAddressDataAllowed.value = isReloadCustomerDataAllowed.value && !!selectedDocument.value?.delivery_address_ref
  if (isSaleDocumentLocked(e?.data) || e.data?.has_rounding_expense_row || e.data?.has_rounding_row) {
    isReadOnly.value = true
    return
  }
}

const onDocumentRowValidating = async(e) => {
  onTabsValidating(e)
  let deliveryAddressSaved = false
  const saveForm = await onRowValidating(e, gridRef, false)

  const invalidFieldsName = e.brokenRules.map(rule => columns.find(col => col.caption === rule.validator._validationInfo.result.name)?.dataField).filter(Boolean)
  if (!e.isValid && e.brokenRules?.length > 0 && e.brokenRules.length === invalidFieldsName.length && invalidFieldsName.every(field => field === 'delivery_address_ref')) {
    e.component.beginCustomLoading()
    e.component.option('disabled', true)

    try {
      const isValidDeliveryAddress = !isSaveDisabled('delivery_address', disabledDeliveryAddressFields, addressRequiredFields)
      if (isValidDeliveryAddress && !(e.oldData && (e.oldData?.delivery_address?.id && e.newData?.delivery_address?.id !== null))) {
        const res = await saveNewCustomerDeliveryAddress()
        deliveryAddressSaved = !!res?.id
      }
    } finally {
      e.component.endCustomLoading()
      e.component.option('disabled', false)
    }
  }

  if (saveForm && deliveryAddressSaved) {
    e.component.saveEditData()
  }
}

const dataSource = dataGrid.useODataSource(saleApi.getAbsoluteUrl() + saleApi.documentsUrl)
const gridOptions = computed(() => dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: dataSource,
  columns: columns,
  editing: editing,
  selection: dataGrid.useDefaultGridSelection(),
  onClearFilter: onClearFilter,
  onInitNewRow: onInitNewRow,
  onRowInserting: onRowInserting,
  onEditCanceled: onEditCanceled,
  onRowUpdating: onRowUpdating,
  onEditorPreparing: onEditorPreparing,
  onEditingStart: onEditingStart,
  onSelectionChanged: onSelectionChanged,
  onFocusedRowChangedDebounced: onFocusedRowChangedDebounced,
  onToolbarPreparing: onToolbarPreparing,
  onSaving: onSaving,
  onSaved: onSaved,
  focusGrid: focusGrid,
  onRowValidating: onDocumentRowValidating,
  splittedGrid: true,
  routerFilter: {
    fields: [{series: 'series'}, {number: 'number_mnemonic'}, {type: 'sale_document_type'}],
    getRoute: () => route,
    router: router
  },
}))

onMounted(() => {
  saleDocumentTypes.value = Object.entries(portalStore.documentTypePageMap)
  .filter(([_, page]) => page === props.type)
  .map(([id]) => parseInt(id))

  dataSource.filter([saleDocumentTypes.value.map(id => `(saleDocumentType eq ${id})`).join(' or ')])
})

const getGeneratingDocumentRefData = (saleDocumentData, worksheetData) => {
  const data = saleDocumentData?.id ? saleDocumentData : worksheetData

  return {
    series: data.series,
    number: data.number_mnemonic,
    type: data.type
  }
}

const getGeneratingDocumentTooltipData = (cellInfo) => {
  const data = cellInfo.data?.source_worksheet?.id ? cellInfo.data.source_worksheet : cellInfo.data?.source_sale_document
  const type = cellInfo.data?.source_sale_document?.type ? cellInfo.data?.source_sale_document?.type : portalStore.documentTypesEnum.GARAGE_WORKSHEET

  return { ...data, type }
}

const getGeneratedDocumentTooltipData = (cellInfo) => {
  return { ...cellInfo.data?.generated_document, type: cellInfo.data?.generated_document?.type }
}

const getReferenceLinkTarget = (type) => {
  if (!type) {
    return menuRoutesEnum.WORKSHEETS
  }
  return getDocumentRouteByDocumentType(type)
}

</script>

<template>
  <div>
    <document-generate-dialog
      v-if="generateDialogVisible"
      v-model:visible="generateDialogVisible"
      :generate-document-type="generateDocumentType"
      :generating-document-type="generatingDocumentMap"
      :generate-function="saleStore.generateSaleDocumentAsync"
      :next-id-function="(date, documentType) => saleStore.api.getSaleDocumentNextIdByDataAsync({ date, documentType })"
      :selected-item="refDocument"
      @created="onDocumentGenerated"
      @cancel="refQuote = null"
    />
    <print-dialog
      v-model:visible="print.printDialogVisible.value"
      :selected-rows="selectedRows"
      :entity="portalStore.printEntities.SALE_DOCUMENT"
      :filename="getLocalizedFileName()"
      :print-function="saleStore.api.printSaleDocumentsAsync"
      :print-preview-function="(options) => saleStore.api.printPreviewSaleDocumentAsync(options)"
      :handle-warning-messages="handleWarningMessages"
    />

    <dx-splitter
      orientation="vertical"
      height="90vh"
      @resize-end="e => onSplitterResize(e, mainSplitterId)"
      @item-collapsed="e => onSplitterResize(e, mainSplitterId)"
      @initialized="e => restoreSplitterSize(e, mainSplitterId)"
    >
      <dx-item
        min-size="21%"
        :collapsible="true"
        collapsed-size="21%"
      >
        <dx-data-grid
          ref="gridRef"
          v-bind="gridOptions"
        >
          <template #attachmentsCellTemplate="{ data: cellInfo }">
            <attachment-icon-list
              :data="cellInfo.data"
              document-name="common.saleDocument"
              :has-attachments="cellInfo.data?.has_attachments"
              :document-field="'attachment_saledocument'"
              :form-data-source="{
                attachment_category: portalStore.attachmentCategoryEnum.SALE_DOCUMENT,
              }"
              :entities-id="getAttachmentEntities(cellInfo.data, ['sale_document', 'subject'])"
              :refresh-grid-async="() => refreshGridAsync()"
            />
          </template>
          <template #gridLayoutConfigTemplate>
            <grid-layout-config-template
              :grid-ext-ref="gridRef"
              :grid-ext-id="gridId"
            />
          </template>
          <template #validationTabTemplate="{data: templateOptions}">
            <validation-tab
              :title="templateOptions.title"
              :has-error="validationTabIndexErrors.includes(templateOptions.tabIndex)"
            />
          </template>
          <template #generic-caption-template="{ data: cellInfo }">
            <div class="dx-form-group-caption-wrapper">
              <q-icon
                :name="cellInfo.name"
                class="dx-form-group-caption-icon"
              />
              <span class="dx-form-group-caption-text">
                {{ cellInfo.caption }}
              </span>
            </div>
          </template>
          <template #customer-info-caption-template="{ data: cellInfo }">
            <div
              class="dx-form-group-caption"
              style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
            >
              <div class="dx-form-group-caption-wrapper">
                <q-icon
                  :name="cellInfo.name"
                  class="dx-form-group-caption-icon"
                />
                <span class="dx-form-group-caption-text">
                  {{ cellInfo.caption }}
                </span>
              </div>
              <div>
                <q-btn
                  icon="mdi-database-refresh"
                  flat
                  size="xs"
                  round
                  :disable="!isReloadCustomerDataAllowed"
                  @click="reloadCustomer"
                />
                <q-btn
                  icon="mdi-content-save"
                  flat
                  size="xs"
                  round
                  :disable="isCustomerSaveDisabled"
                  @click="saveNewCustomer"
                />
              </div>
            </div>
          </template>
          <template #delivery-address-info-caption-template="{ data: cellInfo }">
            <div
              class="dx-form-group-caption"
              style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
            >
              <div class="dx-form-group-caption-wrapper">
                <q-icon
                  :name="cellInfo.name"
                  class="dx-form-group-caption-icon"
                />
                <span class="dx-form-group-caption-text">
                  {{ cellInfo.caption }}
                </span>
              </div>
              <div>
                <q-btn
                  icon="mdi-database-refresh"
                  flat
                  size="xs"
                  round
                  :disable="!isReloadDeliveryAddressDataAllowed"
                  @click="reloadAddress"
                />
                <q-btn
                  icon="mdi-content-save"
                  flat
                  size="xs"
                  round
                  :disable="isDeliveryAddressSaveDisabled"
                  @click="saveNewCustomerDeliveryAddress"
                />
              </div>
            </div>
          </template>
          <template #multiSelectFilterRef="{ data }">
            <multi-select-filter
              :ref="el => setMultiSelectFilterRef(data.component.option('field'), el)"
              :data-source="getFilterDataSource(data.component.option('field'))"
              :field="data.component.option('field')"
              :grid-ref="gridRef"
              :apply-filter="applyFilter"
              :main-filter="[saleDocumentTypes.map(id => `(saleDocumentType eq ${id})`).join(' or ')]"
              v-bind="{ data }"
            />
          </template>
          <template #depositEditor="{ data: cellInfo }">
            <deposit-drop-down-box
              :data="cellInfo"
              :disabled="isReadOnly || cellInfo?.data?.rows_count > 0"
            />
          </template>
          <template #mechanicsEditor="{ data }">
            <mechanic-drop-down-box
              :data="data"
              :disabled="isReadOnly || data.data?.rows_count > 0"
            />
          </template>
          <template #mechanicsAvatarList="{ data }">
            <mechanics-avatar-list :ids="data.value" />
          </template>
          <template #customerEditor="{ data: cellInfo }">
            <customer-drop-down-box
              show-clear-button
              :data="cellInfo"
              :disabled="isReadOnly || cellInfo?.data?.rows_count > 0"
            />
          </template>
          <template #billingCustomerEditor="{ data: cellInfo }">
            <customer-drop-down-box
              show-clear-button
              :data="cellInfo"
              :exclude="cellInfo.data?.customer_id"
              :disabled="isReadOnly || isBillingCustomerDisabled || !cellInfo.data?.customer_id"
            />
          </template>
          <template #carrierEditor="{ data: cellInfo }">
            <supplier-drop-down-box
              show-clear-button
              :data="cellInfo"
              :disabled="isReadOnly"
              supplier_type_code="CR"
            />
          </template>
          <template #deliveryAddressEditor="{ data: cellInfo }">
            <delivery-address-drop-down-box
              show-clear-button
              :data="cellInfo"
              :customer="currentCustomer"
              :disabled="isReadOnly || !currentCustomer?.id"
            />
          </template>
          <template #countryEditor="{ data: cellInfo }">
            <country-drop-down-box
              :data="cellInfo"
              :is-portal="['customer.vat_country_id'].includes(cellInfo.column?.dataField)"
              :disabled="isReadOnly || !!cellInfo.data.customer?.id"
              @value-changed="onCountryChanged($event)"
            />
          </template>
          <template #vatCountryEditor="{ data: cellInfo }">
            <country-drop-down-box
              :data="cellInfo"
              :is-portal="['customer.vat_country_id'].includes(cellInfo.column?.dataField)"
              :disabled="isReadOnly || !!cellInfo.data.customer?.id"
              @value-changed="onCountryChanged($event, cellInfo.column?.dataField, type='vat_country')"
            />
          </template>
          <template #stateEditor="{ data: cellInfo }">
            <state-drop-down-box
              :data="cellInfo"
              :value="cellInfo.data.customer?.address?.state"
              :value-text="cellInfo.data.customer?.address?.state_text"
              :country="currentCountryId"
              :disabled="!!cellInfo.data.customer?.id || cellInfo.data?.has_worksheet"
              @value-changed="onStateChanged($event)"
            />
          </template>
          <template #deliveryAddressStateEditor="{ data: cellInfo }">
            <state-drop-down-box
              :data="cellInfo"
              :value="cellInfo.data.delivery_address?.address?.state"
              :value-text="cellInfo.data.delivery_address?.address?.state_text"
              :country="currentDeliveryCountryId"
              :disabled="isReadOnly || !currentCustomer?.id || !!cellInfo.data?.delivery_address_ref"
              @value-changed="onStateChanged($event, type='delivery_address')"
            />
          </template>
          <template #deliveryAddressCountryEditor="{ data: cellInfo }">
            <country-drop-down-box
              :data="cellInfo"
              :is-portal="['customer.vat_country_id'].includes(cellInfo.column?.dataField)"
              :disabled="isReadOnly || !currentCustomer?.id || !!cellInfo.data.delivery_address_ref"
              @value-changed="onCountryChanged($event, null, type='delivery_address')"
            />
          </template>
          <template #cityEditor="{ data: cellInfo }">
            <city-drop-down-box
              :data="cellInfo"
              :value="cellInfo.data.customer?.address?.city"
              :value-text="cellInfo.data.customer?.address?.city_text"
              :country="currentCountryId"
              :state="currentStateId"
              :zip-code="currentZipCode"
              :filtered-cities-count="currentFilteredCitiesCount"
              :disabled="isReadOnly || !!cellInfo.data.customer?.id"
            />
          </template>
          <template #deliveryAddressCityEditor="{ data: cellInfo }">
            <city-drop-down-box
              :data="cellInfo"
              :value="cellInfo.data.delivery_address?.address?.city"
              :value-text="cellInfo.data.delivery_address?.address?.city_text"
              :country="currentDeliveryCountryId"
              :state="currentDeliveryStateId"
              :zip-code="currentDeliveryZipCode"
              :filtered-cities-count="currentDeliveryFilteredCitiesCount"
              :disabled="isReadOnly || !currentCustomer?.id || !!cellInfo.data.delivery_address_ref"
            />
          </template>
          <template #vehicleEditor="{ data: cellInfo }">
            <vehicle-drop-down-box
              show-clear-button
              :data="cellInfo"
              :disabled="isReadOnly || !!cellInfo.data?.source_worksheet?.id"
            />
          </template>
          <template #typeCellTemplate="{ data: templateOptions }">
            <q-chip
              size="sm"
              class="q-ma-none"
              text-color="white"
              style="margin: 0"
              :label="localizedDocumentTypes.find(x => x.id === templateOptions.data.sale_document_type)?.name"
              :style="{ backgroundColor: localizedDocumentTypes.find(x => x.id === templateOptions.data.sale_document_type)?.color }"
            />
          </template>
          <template #documentTypeCellTemplate="{ data: templateOptions }">
            <q-chip
              size="sm"
              class="q-ma-none"
              text-color="white"
              style="margin: 0"
              :label="localizedDocumentTypes.find(x => x.id === templateOptions.data.sale_document_type)?.name"
              :style="{ backgroundColor: localizedDocumentTypes.find(x => x.id === templateOptions.data.sale_document_type)?.color }"
            />
          </template>
          <template #tagsEditor>
            <document-tags-tag-box
              :tags="allComputedTags"
              :value="allComputedTags.map(x => x.id)"
              disabled
            />
          </template>
          <template
            #editButtonsTemplate="{ data: cellInfo }"
          >
            <edit-lock-buttons-template
              :entity-name="'SaleDocument'"
              :cell-info="cellInfo"
              :grid="gridRef"
              :entity-lock="entityLock"
              :is-locked-available="true"
              :buttons="rowButtons"
            />
          </template>
          <template #customToolbarAddButtonTemplate>
            <custom-toolbar-add-button-template
              :grid="gridRef"
              :entity-lock="entityLock"
            />
          </template>
          <template #customToolbarDeleteButtonTemplate>
            <custom-toolbar-delete-button-template
              :selected-rows-id="selectedRows?.map(i => i.id) || []"
              :grid="gridRef"
              :entity-lock="entityLock"
              :on-delete="batchDeleteAsync"
              :disabled="selectedRows.length === 0 || selectedRows?.some(i => i.has_sale_document || i.has_rounding_expense_row || i.has_rounding_row) || (!selectedRows.every(row => !!row?.electronic_invoice_progress_status && portalStore.electronicInvoiceEditableFamilies.includes(portalStore.getElectronicInvoiceFamilyByProgressStatus(row.electronic_invoice_progress_status))))"
            />
          </template>
          <template #generatingDocumentRef="{ data: cellInfo }">
            <reference-link
              v-if="!!cellInfo.data.source_sale_document?.id || !!cellInfo.data.source_worksheet?.id"
              :data="getGeneratingDocumentRefData(cellInfo.data.source_sale_document, cellInfo.data.source_worksheet)"
              :text-fields="['prefix', 'series', 'number']"
              :query-fields="['series', 'number', 'type']"
              :target="getReferenceLinkTarget(cellInfo.data.source_sale_document?.type)"
              :fetch-document-relations="() => saleApi.getDocumentRelationTreeAsync(cellInfo.data.id, cellInfo.data.sale_document_type)"
            >
              <information-tool-tip :data="extractReferenceData(getGeneratingDocumentTooltipData(cellInfo))" />
            </reference-link>
          </template>
          <template #generatedDocumentRef="{ data: cellInfo }">
            <reference-link
              v-if="!!cellInfo.data.generated_document?.id"
              :data="getGeneratingDocumentRefData(cellInfo.data.generated_document)"
              :text-fields="['prefix', 'series', 'number']"
              :query-fields="['series', 'number', 'type']"
              :target="getReferenceLinkTarget(cellInfo.data.generated_document?.type)"
              :fetch-document-relations="() => saleApi.getDocumentRelationTreeAsync(cellInfo.data.id, cellInfo.data.sale_document_type)"
            >
              <information-tool-tip :data="extractReferenceData(getGeneratedDocumentTooltipData(cellInfo))" />
            </reference-link>
          </template>
        </dx-data-grid>
      </dx-item>
      <dx-item
        min-size="21%"
        :collapsible="true"
        collapsed-size="21%"
      >
        <div style="height: 100%; width: 100%;">
          <sale-document-row-grid
            v-if="selectedDocument"
            :data="selectedDocument"
            :entity-lock="entityLock"
            :read-only="(entityLock.isLocked(false, selectedDocument.id) && !entityLock.isLocked(true, selectedDocument.id)) || isSaleDocumentLocked(selectedDocument) || selectedDocument.has_rounding_row"
            class="custom-padding-sale-document-row-grid"
            :is-locked="(e) => isSaleDocumentLocked(e)"
            @row-saved="onDetailRowSaved"
            @content-ready="onDetailContentReady"
          />
          <div
            v-else
            style="min-height: 1px;"
          />
        </div>
      </dx-item>
    </dx-splitter>
  </div>
</template>

<style scoped lang="scss">
.dx-form-group-caption-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.dx-form-group-caption-wrapper .dx-form-group-caption-icon {
  font-size: 1.2rem;
  line-height: 1;
}
.dx-form-group-caption-wrapper .dx-form-group-caption-text {
  line-height: 1.2;
}
</style>
